import { SortingsDefinition } from '@factorialco/f0-react'
import { useApolloClient } from '@apollo/client'
import {
  OneDataCollection,
  SummariesDefinition,
  type InFilterDefinition,
} from '@factorialco/f0-react/dist/experimental'
import {
  HeadcountSummariesDocument,
  type PlanningValueObjectsRequestStatusEnum,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'
import { useEffect, useRef } from 'react'

import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import useHeadcountRequestMutations from 'modules/planning/hooks/headcount/useHeadcountRequestMutations'
import useHeadcountStatusMutations from 'modules/planning/hooks/headcount/useHeadcountStatusMutations'
import useHeadcountChildren from 'modules/planning/hooks/headcount/useHeadcountChildren'
import useHeadcountJobCatalogSource from 'modules/planning/hooks/headcount/useHeadcountJobCatalogSource'
import useHeadcountEmployeeSource from 'modules/planning/hooks/headcount/useHeadcountEmployeeSource'
import useHeadcountLocationSource from 'modules/planning/hooks/headcount/useHeadcountLocationSource'
import {
  mapSalaryFilterToVariables,
  mapWorkingTimeFilterToVariables,
} from 'modules/planning/lib/mapWorkingTimeFilterToVariables'
import { getCapabilities, type PeriodRoleContext } from 'modules/planning/roles'
import { PLANNING_PERIOD_STATUS } from 'modules/planning/consts/period_status'
import { getColumnDefinition } from 'modules/planning/roles/columnDefinition'
import { type HeadcountRow } from 'modules/planning/types/headcount'
import {
  defaultParentRow,
  PARENT_NEW_HEADCOUNT,
  PARENT_ACTUAL_HEADCOUNT,
} from 'modules/planning/consts/headcount'
import { type NumberFilterDefinition } from '@factorialco/f0-react/dist/experimental'
import { getEmployeeLabelById } from 'modules/employees/lib/getEmployeeLabelById'
import { useJobCatalogRoleFilter } from 'modules/job_catalog/lib/filters/jobRole'
import useHasJobCatalogTree from 'modules/planning/hooks/headcount/useHasJobCatalogTree'

interface Props {
  periodId: string
  planningId?: string
  submissionPeriodId?: string
  companyId?: string
  canCreateRequest: boolean
  roleContext?: PeriodRoleContext
  periodStatus?: string
  initialManagerId?: string
}

const DEFAULT_CURRENCY = 'EUR'

type BulkActionItemsState = {
  itemsStatus: readonly { checked: boolean; item: HeadcountRow }[]
}

type HeadcountFilters = {
  managerId: InFilterDefinition<string>
  locationIds: InFilterDefinition<string>
  directManagerIds: InFilterDefinition<string>
  jobRole: InFilterDefinition<string>
  statuses: InFilterDefinition<PlanningValueObjectsRequestStatusEnum>
  workingTime: NumberFilterDefinition
  salary: NumberFilterDefinition
}

export const HeadcountEmployeeSheet = ({
  periodId,
  planningId,
  submissionPeriodId,
  companyId,
  canCreateRequest,
  roleContext,
  periodStatus,
  initialManagerId,
}: Props) => {
  const apolloClient = useApolloClient()
  const { company, employee } = useCurrentSession()

  const hasJobCatalogTreeFeature = useHasJobCatalogTree()
  const jobCatalogRoleFilter = useJobCatalogRoleFilter({ hasJobCatalogTreeFeature })

  const mainLegalEntityCurrency =
    company.legalEntities?.find((entity) => entity.main)?.currency ??
    company.currency ??
    DEFAULT_CURRENCY
  const { bulkCreate, bulkUpdate, bulkDelete } = useHeadcountRequestMutations({
    periodId,
    planningId,
    submissionPeriodId,
    companyId,
  })
  const { bulkUpdateStatus } = useHeadcountStatusMutations()
  const { fetchChildren } = useHeadcountChildren({ periodId, planningId })
  const capabilities = getCapabilities(roleContext?.roles ?? [])
  const canApproveReject = capabilities.has('approveRejectRequest')
  const canDeleteOwnRequest = canApproveReject || periodStatus === PLANNING_PERIOD_STATUS.ACTIVE

  useOneMutationSubscription('BulkCreatePlanningRequests', HeadcountSummariesDocument, true)

  const isOnlyPlanner = roleContext?.higherRole === 'planner'

  const headcountStatusFilterOptions: {
    value: PlanningValueObjectsRequestStatusEnum
    label: string
  }[] = [
    { value: 'approved', label: i18n.t('planning.employee_sheet.approval_status.approved') },
    { value: 'pending', label: i18n.t('planning.employee_sheet.approval_status.pending') },
    { value: 'rejected', label: i18n.t('planning.employee_sheet.approval_status.rejected') },
    { value: 'draft', label: i18n.t('planning.employee_sheet.approval_status.draft') },
  ]

  const initialManagerFilter = initialManagerId ? [String(initialManagerId)] : undefined

  const reporteesEmployeeSource = useHeadcountEmployeeSource({
    selfAndReportees: isOnlyPlanner,
  })

  const locationSource = useHeadcountLocationSource()
  const jobCatalogSource = useHeadcountJobCatalogSource()

  const getEmployeeLabel = (value: unknown) => getEmployeeLabelById(value, apolloClient)

  const isPendingRequest = (item: HeadcountRow) =>
    item.rowKind === 'headcount_request' && item.status === 'pending'

  const isOwnRequest = (item: HeadcountRow) =>
    item.rowKind === 'headcount_request' && item.employeeId === employee.id

  const columns = getColumnDefinition({
    jobCatalogSource,
    reporteesEmployeeSource,
    locationSource,
    currency: mainLegalEntityCurrency,
    periodStatus,
    capabilities,
    currentEmployeeId: employee.id,
  })

  const bulkUpdateRef = useRef(bulkUpdate)
  const currencyRef = useRef(mainLegalEntityCurrency)

  useEffect(() => {
    bulkUpdateRef.current = bulkUpdate
  }, [bulkUpdate])

  useEffect(() => {
    currencyRef.current = mainLegalEntityCurrency
  }, [mainLegalEntityCurrency])

  // --- Debounced update ---
  const debouncedUpdate = useUnsafeMemo(
    () =>
      debounce(async (updatedItem: HeadcountRow) => {
        if (updatedItem.rowKind !== 'headcount_request') return

        await bulkUpdateRef.current({
          id: updatedItem.id,
          jobCatalogTreeNodeUuid: updatedItem.jobCatalogTreeNodeUuid || undefined,
          oldJobCatalogLevelId: updatedItem.oldJobCatalogLevelId || undefined,
          locationId: updatedItem.locationId,
          managerId: updatedItem.managerId,
          salary: updatedItem.salary ? Number(updatedItem.salary) : undefined,
          workingTime: updatedItem.workingTime ? Number(updatedItem.workingTime) : undefined,
          startDate: updatedItem.startDate,
          currency: currencyRef.current || undefined,
        })
      }, 250),
    []
  )

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])
  // --- Cell change handler ---
  const handleCellChange = async (updatedItem: HeadcountRow) => {
    if (updatedItem.rowKind !== 'headcount_request') return

    debouncedUpdate.cancel()
    debouncedUpdate(updatedItem)
  }

  // --- Add row handler ---
  const handleAddNewHeadcountRow = async () => {
    await bulkCreate()
  }

  // --- Bulk approve/reject/delete handler ---
  const handleBulkAction = async (action: string, selectedItems: BulkActionItemsState) => {
    if (action === 'delete') {
      const ownSelectedIds = selectedItems.itemsStatus
        .filter(({ checked, item }) => checked && isOwnRequest(item))
        .map(({ item }) => item.id)

      if (ownSelectedIds.length === 0) return
      await bulkDelete(ownSelectedIds)
      return
    }

    const pendingSelectedIds = selectedItems.itemsStatus
      .filter(({ checked }) => checked)
      .map(({ item }) => item.id)

    if (pendingSelectedIds.length === 0) return

    if (action === 'approve') {
      await bulkUpdateStatus(pendingSelectedIds.map((id) => ({ id, status: 'approved' as const })))
    } else if (action === 'reject') {
      await bulkUpdateStatus(pendingSelectedIds.map((id) => ({ id, status: 'rejected' as const })))
    }
  }

  // --- Data source ---
  const source = useGraphqlDataCollectionSource<
    HeadcountRow,
    HeadcountFilters,
    SortingsDefinition,
    SummariesDefinition,
    typeof HeadcountSummariesDocument
  >(
    {
      document: HeadcountSummariesDocument,
      mapData: (data) => {
        const summaries = data.planning.headcountSummariesConnection.nodes
        const newHeadcountCount = summaries[0]?.newHeadcountCount ?? 0
        const actualHeadcountCount = summaries[0]?.actualHeadcountCount ?? 0
        const rootEmployeeId = summaries[0]?.actualHeadcountRootEmployeeId ?? undefined

        return [
          {
            ...defaultParentRow,
            id: PARENT_NEW_HEADCOUNT,
            parentType: PARENT_NEW_HEADCOUNT,
            parentWithChildren: newHeadcountCount > 0 || canCreateRequest,
            label: i18n.t('planning.employee_sheet.new_headcount'),
            count: `${newHeadcountCount}`,
            salary: summaries[0]?.newHeadcountTotalMoney ?? '0',
            currency: mainLegalEntityCurrency ?? '',
          },
          {
            ...defaultParentRow,
            id: PARENT_ACTUAL_HEADCOUNT,
            parentType: PARENT_ACTUAL_HEADCOUNT,
            parentWithChildren: actualHeadcountCount > 0,
            label: i18n.t('planning.employee_sheet.actual_headcount'),
            count: `${actualHeadcountCount}`,
            salary: summaries[0]?.actualHeadcountTotalMoney ?? '0',
            currency: mainLegalEntityCurrency ?? '',
            rootEmployeeId,
          },
        ]
      },
      mapVariables: ({ filters, search }) => {
        const workingTimeFilters = mapWorkingTimeFilterToVariables(filters?.workingTime)
        const salaryFilters = mapSalaryFilterToVariables(filters?.salary)
        const jobCatalogLevelIds = !hasJobCatalogTreeFeature
          ? filters?.jobRole?.map((id) => String(id))
          : undefined
        const jobCatalogTreeLevelUuids = hasJobCatalogTreeFeature
          ? filters?.jobRole?.map((id) => id.toString())
          : undefined
        const summaryFilters = {
          search,
          locationIds: filters?.locationIds,
          directManagerIds: filters?.directManagerIds,
          jobCatalogLevelIds,
          jobCatalogTreeLevelUuids,
          statuses: filters?.statuses?.length ? filters.statuses : undefined,
          ...workingTimeFilters,
          ...salaryFilters,
        }
        const hasSummaryFilters = Object.values(summaryFilters).some((value) => value !== undefined)

        return {
          planningPeriodId: String(periodId),
          planningPlanningId: planningId,
          managerId: filters?.managerId ? String(filters.managerId) : undefined,
          filters: hasSummaryFilters ? summaryFilters : undefined,
        }
      },
      mapPagination: () => ({ total: 2 }),
      paginationType: 'pages',
      currentFilters: initialManagerFilter ? { managerId: initialManagerFilter } : undefined,
      filters: {
        managerId: {
          type: 'in',
          label: i18n.t('planning.employee_sheet.filters.employee'),
          options: {
            source: reporteesEmployeeSource,
            mapOptions: (emp: { id: string; fullName: string }) => ({
              value: emp.id,
              label: emp.fullName,
            }),
            getLabel: getEmployeeLabel,
          },
        },
        directManagerIds: {
          type: 'in',
          label: i18n.t('planning.employee_sheet.filters.manager'),
          options: {
            source: reporteesEmployeeSource,
            mapOptions: (emp: { id: string; fullName: string }) => ({
              value: emp.id,
              label: emp.fullName,
            }),
          },
        },
        locationIds: {
          type: 'in',
          label: i18n.t('planning.employee_sheet.filters.workplace'),
          options: {
            source: locationSource,
            mapOptions: (loc: { id: string; name: string }) => ({
              value: loc.id,
              label: loc.name,
            }),
          },
        },
        jobRole: jobCatalogRoleFilter,
        statuses: {
          type: 'in',
          label: i18n.t('planning.employee_sheet.filters.status'),
          options: {
            options: headcountStatusFilterOptions,
          },
        },
        workingTime: {
          type: 'number',
          label: i18n.t('planning.employee_sheet.filters.working_time'),
          options: {
            min: 0,
            max: 100,
            modes: ['range', 'single'],
            openCloseToggle: true,
          },
        },
        salary: {
          type: 'number',
          label: i18n.t('planning.employee_sheet.filters.salary'),
          options: {
            min: 0,
            modes: ['range', 'single'],
            openCloseToggle: true,
          },
        },
      },
      itemsWithChildren: (item) => {
        if (item.rowKind !== 'parent') return false

        return item.parentWithChildren
      },
      fetchChildren,
      selectable: (item: HeadcountRow) => {
        const isSelectableRequest = item.rowKind === 'headcount_request'

        if (canApproveReject && isSelectableRequest) return item.id
        if (
          isOwnRequest(item) &&
          isSelectableRequest &&
          isPendingRequest(item) &&
          canDeleteOwnRequest
        )
          return item.id
        return undefined
      },
      allPagesSelection: true,
      bulkActions: () => ({
        primary: canApproveReject
          ? [
              {
                id: 'approve',
                label: i18n.t('planning.employee_sheet.actions.approve'),
              },
            ]
          : [],
        secondary: [
          ...(canApproveReject
            ? [
                {
                  id: 'reject',
                  label: i18n.t('planning.employee_sheet.actions.reject'),
                },
              ]
            : []),
          ...(canDeleteOwnRequest
            ? [
                {
                  id: 'delete',
                  label: i18n.t('planning.employee_sheet.actions.delete'),
                },
              ]
            : []),
        ],
      }),
    },
    [
      canCreateRequest,
      canApproveReject,
      canDeleteOwnRequest,
      initialManagerId,
      periodId,
      planningId,
    ]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      {...(canApproveReject || canDeleteOwnRequest ? { onBulkAction: handleBulkAction } : {})}
      visualizations={[
        {
          type: 'editableTable' as const,
          options: {
            // @ts-expect-error f0-react EditableTableColumnDefinition has invariant generics on selectConfig.source (Map variance)
            columns: columns,
            frozenColumns: 1,
            onCellChange: handleCellChange,
            addNestedRowActions: (parent: HeadcountRow) => {
              if (!canCreateRequest) return undefined

              return parent.parentType === PARENT_NEW_HEADCOUNT
                ? [
                    {
                      label: i18n.t('common.add'),
                      onClick: handleAddNewHeadcountRow,
                    },
                  ]
                : undefined
            },
            addNestedRowActionsLabel: i18n.t('common.add'),
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.employee_sheet.actual_headcount_empty.title'),
          description: i18n.t('planning.employee_sheet.actual_headcount_empty.description'),
        },
      }}
    />
  )
}
